// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Alert, Anchor, Badge, Box, Button, Center, Container, Stack, Text, TextInput, Title } from '@/components/ui';
import { IconMail, IconArrowLeft, IconKey, IconCheck, Icon3dCubeSphere } from '@tabler/icons-react';
import { FadeIn } from '@/components/animations';
import { Floating3DCard } from '../components';

export default function ForgotMinimal3D() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: isDark
          ? `linear-gradient(180deg, ${theme.colors.dark[8]} 0%, ${theme.colors.dark[9]} 100%)`
          : `linear-gradient(180deg, ${theme.colors.gray[0]} 0%, ${theme.colors.gray[1]} 100%)`,
        position: 'relative',
      }}
    >
      {/* Subtle pattern */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(${isDark ? theme.colors.dark[6] : theme.colors.gray[3]} 1px, transparent 1px)`,
          backgroundSize: '32px 32px',
          opacity: 0.5,
          pointerEvents: 'none',
        }}
      />

      <Center mih="100vh" p="xl" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <FadeIn delay={100}>
            <Floating3DCard
              maxTilt={10}
              floatingShadow
              glow
              glowColor={theme.colors.orange[5]}
              radius="lg"
            >
              {submitted ? (
                <Stack gap="lg" align="center">
                  <Center
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 20,
                      backgroundColor: 'var(--mantine-color-green-light)',
                    }}
                  >
                    <IconCheck size={40} color="var(--mantine-color-green-6)" />
                  </Center>

                  <Stack align="center" gap="xs">
                    <Title order={2} ta="center">Check your email</Title>
                    <Text c="dimmed" ta="center" maw={300}>
                      We've sent a password reset link to <strong>{email}</strong>
                    </Text>
                  </Stack>

                  <Alert color="blue" variant="light" w="100%">
                    <Text size="sm">
                      Didn't receive the email? Check your spam folder or{' '}
                      <Anchor onClick={() => setSubmitted(false)}>try again</Anchor>
                    </Text>
                  </Alert>

                  <Button
                    component={Link}
                    to="/demo/auth-pages/login/minimal3d"
                    variant="subtle"
                    leftSection={<IconArrowLeft size={16} />}
                  >
                    Back to login
                  </Button>
                </Stack>
              ) : (
                <Stack gap="lg">
                  <Button
                    component={Link}
                    to="/demo/auth-pages/forgot-password"
                    variant="subtle"
                    size="xs"
                    leftSection={<IconArrowLeft size={14} />}
                    w="fit-content"
                  >
                    Back to variants
                  </Button>

                  <Stack align="center" gap="xs">
                    <Badge
                      leftSection={<Icon3dCubeSphere size={12} />}
                      variant="gradient"
                      gradient={{ from: 'gray', to: 'dark' }}
                      size="lg"
                    >
                      Minimal 3D
                    </Badge>
                    <Center
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 14,
                        backgroundColor: 'var(--mantine-color-orange-light)',
                        marginTop: 'var(--mantine-spacing-sm)',
                      }}
                    >
                      <IconKey size={28} color="var(--mantine-color-orange-6)" />
                    </Center>
                    <Title order={2} ta="center" mt="sm">
                      Forgot password?
                    </Title>
                    <Text c="dimmed" size="sm" ta="center" maw={300}>
                      No worries, we'll send you reset instructions.
                    </Text>
                  </Stack>

                  <form onSubmit={handleSubmit}>
                    <Stack gap="md">
                      <TextInput
                        label="Email"
                        placeholder="Enter your email"
                        leftSection={<IconMail size={16} />}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        size="md"
                        radius="md"
                        variant="filled"
                      />

                      <Button
                        type="submit"
                        fullWidth
                        size="md"
                        radius="md"
                        color="dark"
                      >
                        Send reset link
                      </Button>
                    </Stack>
                  </form>

                  <Center>
                    <Anchor component={Link} to="/demo/auth-pages/login/minimal3d" size="sm">
                      <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      Back to login
                    </Anchor>
                  </Center>
                </Stack>
              )}
            </Floating3DCard>
          </FadeIn>
        </Container>
      </Center>
    </Box>
  );
}
